import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  ArrowLeft, Plus, Trash2, Edit3, Save, X, Smartphone,
  Search, ChevronDown, ChevronUp, Package, Layers
} from 'lucide-react';

const BrandsModelsPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError } = useLanguage();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [expandedSeries, setExpandedSeries] = useState({});
  const [newBrand, setNewBrand] = useState('');
  const [newModel, setNewModel] = useState({});
  const [editingBrand, setEditingBrand] = useState(null);
  const [editBrandName, setEditBrandName] = useState('');

  const fetchCatalog = async () => {
    try {
      const res = await api.get('/brands-models');
      setCatalog(res.data);
    } catch (err) {
      console.error('Error fetching catalog:', err);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCatalog(); }, []);

  const addBrand = async () => {
    if (!newBrand.trim()) return;
    try {
      await api.post('/admin/brands-models', { brand: newBrand.trim(), models: [] });
      setNewBrand('');
      fetchCatalog();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('brandsModels.error'));
    }
  };

  const deleteBrand = async (brandId, brandName) => {
    if (!window.confirm(t('brandsModels.deleteBrandConfirm', { name: brandName }))) return;
    try {
      await api.delete(`/admin/brands-models/${brandId}`);
      fetchCatalog();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('brandsModels.error'));
    }
  };

  const renameBrand = async (brandId) => {
    if (!editBrandName.trim()) return;
    try {
      await api.put(`/admin/brands-models/${brandId}`, { brand: editBrandName.trim() });
      setEditingBrand(null);
      setEditBrandName('');
      fetchCatalog();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('brandsModels.error'));
    }
  };

  const addModel = async (brandId, brand) => {
    const model = (newModel[brandId] || '').trim();
    if (!model) return;
    try {
      await api.post('/admin/brands-models', { brand, models: [model] });
      setNewModel(prev => ({ ...prev, [brandId]: '' }));
      fetchCatalog();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('brandsModels.error'));
    }
  };

  const deleteModel = async (brandId, modelName) => {
    try {
      await api.delete(`/admin/brands-models/${brandId}/model/${encodeURIComponent(modelName)}`);
      fetchCatalog();
    } catch (err) {
      alert(tError(err.response?.data?.detail) || t('brandsModels.error'));
    }
  };

  const toggleSeries = (brandId, seriesName) => {
    const key = `${brandId}-${seriesName}`;
    setExpandedSeries(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filtered = catalog.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    if (b.brand.toLowerCase().includes(q)) return true;
    if (b.models?.some(m => m.toLowerCase().includes(q))) return true;
    if (b.series) {
      for (const [sName, sModels] of Object.entries(b.series)) {
        if (sName.toLowerCase().includes(q)) return true;
        if (sModels.some(m => m.toLowerCase().includes(q))) return true;
      }
    }
    return false;
  });

  const totalModels = catalog.reduce((acc, b) => acc + (b.models?.length || 0), 0);
  const totalSeries = catalog.reduce((acc, b) => acc + Object.keys(b.series || {}).length, 0);

  const brandMatchesSearch = (brand) => {
    if (!search) return true;
    return brand.brand.toLowerCase().includes(search.toLowerCase());
  };

  const getFilteredModelsInSeries = (seriesModels, brand) => {
    if (!search || brandMatchesSearch(brand)) return seriesModels;
    const q = search.toLowerCase();
    return seriesModels.filter(m => m.toLowerCase().includes(q));
  };

  const renderSeriesView = (brand) => {
    const series = brand.series || {};
    const hasSeries = Object.keys(series).length > 0;

    if (!hasSeries) {
      return (
        <div className="flex flex-wrap gap-2">
          {(brand.models || []).map(model => (
            <ModelTag key={model} model={model} brandId={brand.id} onDelete={deleteModel} isAdmin={isAdmin} />
          ))}
          {(!brand.models || brand.models.length === 0) && (
            <p className="text-xs text-gray-500 italic">{t('brandsModels.noModels')}</p>
          )}
        </div>
      );
    }

    const brandMatches = brandMatchesSearch(brand);
    return (
      <div className="space-y-1">
        {Object.entries(series).map(([seriesName, seriesModels]) => {
          const filteredModels = getFilteredModelsInSeries(seriesModels, brand);
          if (search && !brandMatches && filteredModels.length === 0) return null;
          const key = `${brand.id}-${seriesName}`;
          const isOpen = expandedSeries[key];

          return (
            <div key={seriesName} className="rounded-lg overflow-hidden" data-testid={`series-${seriesName.replace(/\s+/g, '-')}`}>
              <button
                onClick={() => toggleSeries(brand.id, seriesName)}
                className="w-full flex items-center justify-between px-3 py-2 bg-white/[0.03] hover:bg-white/[0.06] transition-colors rounded-lg"
                data-testid={`series-toggle-${seriesName.replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-2">
                  <Layers className="w-3.5 h-3.5 text-cyan-400/70" />
                  <span className="text-sm font-medium text-cyan-300">{seriesName}</span>
                  <span className="text-xs text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">
                    {filteredModels.length}
                  </span>
                </div>
                {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-gray-500" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-500" />}
              </button>
              {isOpen && (
                <div className="flex flex-wrap gap-1.5 px-3 py-2 ml-5">
                  {filteredModels.map(model => (
                    <ModelTag key={model} model={model} brandId={brand.id} onDelete={deleteModel} isAdmin={isAdmin} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto fade-in space-y-6" data-testid="brands-models-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-gray-300 rounded-xl text-sm hover:bg-white/10 transition-colors"
              data-testid="brands-back-home">
              <ArrowLeft className="w-4 h-4" /> {t('brandsModels.home')}
            </button>
            <Smartphone className="w-6 h-6 text-cyan-400" />
            <h1 className="text-xl font-bold text-white">{t('brandsModels.title')}</h1>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <div className="flex items-center gap-1.5">
              <Package className="w-4 h-4" />
              <span>{t('brandsModels.countBrands', { count: catalog.length })}</span>
            </div>
            <span className="text-gray-600">|</span>
            <span>{t('brandsModels.countSeries', { count: totalSeries })}</span>
            <span className="text-gray-600">|</span>
            <span>{t('brandsModels.countModels', { count: totalModels })}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-cyan-500/50 focus:outline-none"
              placeholder={t('brandsModels.searchPlaceholder')} data-testid="brands-search" />
          </div>
          <div className="flex gap-2">
            <input type="text" value={newBrand} onChange={e => setNewBrand(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addBrand()}
              className="px-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-cyan-500/50 focus:outline-none w-48"
              placeholder={t('brandsModels.newBrandPlaceholder')} data-testid="brands-new-brand-input" />
            <button onClick={addBrand}
              className="px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-colors"
              data-testid="brands-add-brand-btn">
              <Plus className="w-4 h-4" /> {t('brandsModels.addBrand')}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">{t('brandsModels.loading')}</div>
        ) : (
          <div className="space-y-2">
            {filtered.map(brand => {
              const isExpanded = expandedBrand === brand.id;
              const seriesCount = Object.keys(brand.series || {}).length;
              return (
                <div key={brand.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`brand-card-${brand.brand}`}>
                  <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/[0.03] transition-colors"
                    onClick={() => setExpandedBrand(isExpanded ? null : brand.id)}>
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      {editingBrand === brand.id ? (
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <input type="text" value={editBrandName} onChange={e => setEditBrandName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && renameBrand(brand.id)}
                            className="px-2 py-1 bg-white/10 border border-cyan-500/50 rounded-lg text-white text-sm focus:outline-none w-40"
                            autoFocus data-testid={`brand-rename-input-${brand.id}`} />
                          <button onClick={() => renameBrand(brand.id)} className="p-1 text-green-400 hover:text-green-300"><Save className="w-4 h-4" /></button>
                          <button onClick={() => setEditingBrand(null)} className="p-1 text-gray-400 hover:text-gray-300"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <span className="text-white font-semibold text-sm">{brand.brand}</span>
                      )}
                      <div className="flex items-center gap-2">
                        {seriesCount > 0 && (
                          <span className="text-xs text-cyan-400/80 bg-cyan-500/10 px-2 py-0.5 rounded-full">{t('brandsModels.countSeries', { count: seriesCount })}</span>
                        )}
                        <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">{t('brandsModels.countModels', { count: brand.models?.length || 0 })}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => { setEditingBrand(brand.id); setEditBrandName(brand.brand); }}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-white/10 hover:text-cyan-400 transition-colors"
                        title={t('brandsModels.rename')} data-testid={`brand-edit-${brand.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      {isAdmin && (
                      <button onClick={() => deleteBrand(brand.id, brand.brand)}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                        title={t('brandsModels.delete')} data-testid={`brand-delete-${brand.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-white/5 px-4 py-3 space-y-3">
                      <div className="flex gap-2">
                        <input type="text" value={newModel[brand.id] || ''} onChange={e => setNewModel(prev => ({ ...prev, [brand.id]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && addModel(brand.id, brand.brand)}
                          className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-cyan-500/50 focus:outline-none"
                          placeholder={t('brandsModels.addModelPlaceholder', { brand: brand.brand })} data-testid={`brand-new-model-input-${brand.id}`} />
                        <button onClick={() => addModel(brand.id, brand.brand)}
                          className="px-3 py-2 bg-cyan-600/80 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 transition-colors"
                          data-testid={`brand-add-model-btn-${brand.id}`}>
                          <Plus className="w-3.5 h-3.5" /> {t('brandsModels.addModel')}
                        </button>
                      </div>
                      {renderSeriesView(brand)}
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && !loading && (
              <div className="text-center py-8 text-gray-400 text-sm">
                {search ? t('brandsModels.noResults') : t('brandsModels.empty')}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

const ModelTag = ({ model, brandId, onDelete, isAdmin }) => {
  const { t } = useLanguage();
  return (
  <span className="group flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-300 hover:border-white/20 transition-colors">
    {model}
    {isAdmin && (
    <button onClick={() => onDelete(brandId, model)}
      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
      title={t('brandsModels.deleteModel')} data-testid={`model-delete-${model.replace(/\s+/g, '-')}`}>
      <X className="w-3 h-3" />
    </button>
    )}
  </span>
  );
};

export default BrandsModelsPage;
